<template>
  <div>
    <!-- Encabezado con título y botones de acción -->
    <Head :title="`Venta #${order.number_order ?? order.id} ${order.order_state === 'cancel' ? '| PEDIDO ANULADO' : ''}`" />

    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center">
        <button 
          class="mr-4 p-3 text-white bg-teal-800 hover:bg-teal-900 rounded transition-colors" 
          @click="back()"
          title="Volver atrás"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        
        <h1 class="text-3xl font-bold">
          Venta #{{ order.number_order ?? order.id }}
          <span v-if="order.order_state === 'cancel'" class="text-red-500 ml-2">
            PEDIDO ANULADO
          </span>
        </h1>
      </div>

      <!-- Botones de impresión -->
      <div class="flex gap-2">
        <a 
          target="_blank" 
          :href="`/orders/${order.id}/report`"
          class="btn-blue p-3 rounded hover:opacity-90 transition-opacity"
        >
          <i class="fa fa-print text-white mr-2" />
          Presupuesto
        </a>

        <template v-if="order.order_state !== 'estimate'">
          <!-- Nota de entrega con selector de moneda -->
          <div class="inline-flex rounded shadow-sm">
            <a 
              target="_blank"
              :href="`/orders/${order.id}/report-storer/${selectedCurrency}`" 
              class="btn-blue p-3 rounded-l hover:opacity-90 transition-opacity flex items-center"
              :title="`Abrir Nota de Entrega en ${selectedCurrency.toUpperCase()}`"
            >
              <i class="fa fa-print text-white mr-2" />
              Nota de entrega ({{ selectedCurrency.toUpperCase() }})
            </a>
            <select v-model="selectedCurrency" 
              class="bg-blue-800 text-white font-bold text-xs px-2 py-3 rounded-r border-l border-blue-600 outline-none cursor-pointer"
              title="Cambiar moneda de la Nota de Entrega">
              <option value="mixto">Mixto</option>
              <option value="cop">COP</option>
              <option value="bs">Bs</option>
              <option value="usd">USD</option>
            </select>
          </div>

          <a
            target="_blank"
            :href="`/orders/${order.id}/report-invoice`"
            class="btn-blue p-3 rounded hover:opacity-90 transition-opacity"
          >
            <i class="fa fa-print text-white mr-2" />
            Factura
          </a>

          <a
            target="_blank"
            :href="`/pos/${order.id}/show`"
            class="btn-blue p-3 rounded hover:opacity-90 transition-opacity"
          >
            <i class="fa fa-print text-white mr-2" />
            POS
          </a>
        </template>
      </div>
    </div>

    <!-- Barra de Compartir por WhatsApp -->
    <div v-if="order.order_state !== 'estimate'" class="mb-6 p-4 bg-green-50 rounded-lg border border-green-200 shadow flex flex-col md:flex-row items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <i class="fab fa-whatsapp text-green-600 text-2xl"></i>
        <div>
          <span class="font-extrabold text-sm text-green-950 block">Enviar Nota de Entrega por WhatsApp</span>
          <span class="text-xs text-green-700">Moneda del mensaje: <strong>{{ whatsappCurrency.toUpperCase() }}</strong></span>
        </div>
      </div>
      
      <div class="flex flex-wrap items-center gap-2 w-full md:w-auto">
        <div class="flex items-center bg-white border border-gray-300 rounded px-2 py-1 text-xs font-semibold text-gray-700">
          <span class="mr-1 text-gray-500">Moneda:</span>
          <select v-model="whatsappCurrency" class="font-bold border-none outline-none bg-transparent cursor-pointer text-green-900">
            <option value="mixto">Mixto (Multi-moneda)</option>
            <option value="cop">Pesos (COP)</option>
            <option value="bs">Bolívares (Bs)</option>
            <option value="usd">Dólares (USD)</option>
          </select>
        </div>

        <input 
          v-model="numeroWhatsapp" 
          type="text" 
          placeholder="WhatsApp (ej: 0412... o 300...)" 
          class="border border-gray-300 rounded px-3 py-1.5 text-sm w-44 md:w-56" 
        />

        <button 
          @click="sendWhatsApp()"
          type="button"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded shadow transition flex items-center gap-1.5 cursor-pointer text-sm"
        >
          <i class="fab fa-whatsapp"></i>
          <span>Enviar WhatsApp</span>
        </button>
      </div>
    </div>

    <!-- Información del cliente y detalles de la orden -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6 p-6 bg-white rounded-lg shadow">
      <div class="space-y-3">
        <div class="grid grid-cols-2 gap-2">
          <div class="font-semibold">Cliente:</div>
          <div>{{ order.client.name }}</div>

          <div class="font-semibold">Alias/Cédula:</div>
          <div>{{ order.client.identification }}</div>

          <div class="font-semibold">Email:</div>
          <div>{{ order.client.email }}</div>

          <div class="font-semibold">Teléfono:</div>
          <div>{{ order.client.phone }}</div>
        </div>

        <!-- In the general information box, add discount info after the Total Bs line -->
        <!-- In the general information box, update discount info to use orderDiscount -->
        <div class="border-t pt-3 mt-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="font-semibold">Balance USD:</div>
            <div>${{ order.balance.toFixed(2) }}</div>
        
            <div class="font-semibold">Balance Bs:</div>
            <div>{{ (order.balance * change).toFixed(2) }}</div>
        
            <div class="font-semibold">Deuda USD:</div>
            <div>${{ (order.total - order.balance).toFixed(2) }}</div>
        
            <div class="font-semibold">Deuda Bs:</div>
            <div>{{ ((order.total - order.balance) * change).toFixed(2) }}</div>
        
            <!-- Reordered to show Subtotal, Descuento, Total -->
            <div class="font-semibold">Subtotal USD:</div>
            <div>${{ Number(order.subtotal).toFixed(2) }}</div>
            
            <div v-if="order.discount && Number(order.discount) > 0" class="font-semibold text-red-600">Descuento USD:</div>
            <div v-if="order.discount && Number(order.discount) > 0" class="text-red-600">-${{ Number(order.discount).toFixed(2) }}</div>
            
            <div class="font-semibold">Total USD:</div>
            <div>${{ order.total.toFixed(2) }}</div>
        
            <div class="font-semibold">Total Bs:</div>
            <div>{{ (order.total * change).toFixed(2) }}</div>

            <div class="font-semibold">Total Cop:</div>
            <div>{{ (order.total_cop).toFixed(2) }}</div>
          </div>
        </div>

        <div class="border-t pt-3 mt-3">
          <div class="grid grid-cols-2 gap-2">
            <div class="font-semibold">Fecha de venta:</div>
            <div>{{ order?.date }}</div>

            <div class="font-semibold">Tienda:</div>
            <div>{{ order?.organization?.name }}</div>

            <div class="font-semibold">Orden de compra:</div>
            <div>{{ order?.order_buy }}</div>

            <div class="font-semibold">Observaciones:</div>
            <div>{{ order?.comentary }}</div>

            <div class="font-semibold">Modalidad:</div>
            <div>{{ order.isCredit ? 'Crédito' : 'Contado' }}</div>

            <div class="font-semibold">Estado:</div>
            <div>
              <span :class="{
                'text-yellow-600': order.order_state === 'estimate',
                'text-red-600': order.order_state === 'cancel',
                'text-orange-600': order.order_state === 'request',
                'text-green-600': order.order_state === 'finish',
                'text-blue-600': order.order_state === 'pay',
                'text-purple-600': order.order_state === 'delivery'
              }">
                {{ getOrderStateLabel(order.order_state) }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="order.order_state === 'estimate'" class="mt-4">
          <button 
            @click="changeOrderState()"
            class="btn-blue px-4 py-2 hover:opacity-90 transition-opacity"
          >
            Procesar orden
          </button>
        </div>
      </div>

      <div class="text-right">
        <div class="font-medium">
          Vendedor: {{ order?.responsible?.first_name }} {{ order?.responsible?.last_name }}
        </div>
      </div>
    </div>

    <!-- Sección de pagos -->
    <div 
      v-if="canManagePayments"
      class="mb-6 p-6 bg-white rounded-lg shadow"
    >
      <h2 class="text-xl font-bold mb-6">Pagos</h2>

      <payment-box 
        v-if="order.order_state !== 'finish'"
        :quick="true"
        :payment_methods="payment_methods"
        :balance="order.total - order.balance"
        :change="change"
        :order="order"
      />

      <!-- Tabla de pagos -->
      <div class="mt-6">
        <table class="w-full">
          <thead>
            <tr>
              <th class="py-4 px-6 text-left">Método</th>
              <th class="py-4 px-6 text-left">Monto</th>
              <th class="py-4 px-6 text-left">Fecha</th>
              <th class="py-4 px-6 text-left">Referencia</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="pay in order.pays" 
              :key="pay.id"
              class="border-t hover:bg-gray-50 transition-colors"
            >
              <td class="py-3 px-6 capitalize">{{ pay.method }}</td>
              <td class="py-3 px-6">
                {{ pay.amount }}
                <span v-if="pay.amount_bs > 0" class="text-gray-600">
                  (BsD. {{pay.amount_bs}})
                </span>
              </td>
              <td class="py-3 px-6">{{ formatDate(pay.created_at) }}</td>
              <td class="py-3 px-6">{{ pay.reference }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Sección de productos -->
    <div class="p-6 bg-white rounded-lg shadow">
      <h2 class="text-xl font-bold mb-6">Productos</h2>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr>
              <th class="py-4 px-6 text-left">Producto</th>
              <th class="py-4 px-6 text-left">Descripción</th>
              <th class="py-4 px-6 text-left">Depósito</th>
              <th class="py-4 px-6 text-left">Cantidad</th>
              <th class="py-4 px-6 text-left">Precio</th>
              <th class="py-4 px-6 text-left">Precio Cop</th>
              <th class="py-4 px-6 text-left">Total</th>
              <th class="py-4 px-6 text-left">Total Cop</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(product, key) in order.products"
              :key="product.id"
              :class="[
                'border-t',
                product.dispatch ? 'bg-blue-50' : '',
                'hover:bg-gray-50 transition-colors'
              ]"
            >
              <td class="py-3 px-6">{{ product.product.name }}</td>
              <td class="py-3 px-6">{{ product.product.description }}</td>
              <td class="py-3 px-6">{{ product.deposit.name }}</td>
              <td class="py-3 px-6">{{ product.quantity }}</td>
              <td class="py-3 px-6">${{ product.price }}</td>
              <td class="py-3 px-6">{{ product.product.price_cop }} $</td>
              <td class="py-3 px-6">${{ (product.price * product.quantity).toFixed(2) }} (BsD. {{ (product.price * product.quantity) * change }})</td>
              <td class="py-3 px-6">{{ (product.product.price_cop * product.quantity).toFixed(2) }} $</td>
            </tr>
          </tbody>

          <tfoot>
            <tr class="bg-gray-50">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6 font-semibold">Subtotal</td>
              <td class="py-3 px-6">${{ Number(order.subtotal).toFixed(2) }}</td>
            </tr>
            <tr v-if="order.discount && Number(order.discount) > 0" class="bg-gray-50 text-red-600">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6 font-semibold">Descuento</td>
              <td class="py-3 px-6">-${{ Number(order.discount).toFixed(2) }}</td>
            </tr>
            <tr class="bg-gray-50 font-semibold">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6">Total</td>
              <td class="py-3 px-6">${{ Number(order.total).toFixed(2) }}</td>
            </tr>
            <tr class="bg-gray-50 font-semibold">
              <td colspan="6" class="py-3 px-6"></td>
              <td class="py-3 px-6">Total Cop</td>
              <td class="py-3 px-6">${{ Number(order.total_cop).toFixed(2) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="mt-6">
        <button
          v-if="order.order_state === 'delivery'"
          @click="addDispatch"
          class="px-4 py-2 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
        >
          Despachar productos
        </button>
      </div>
    </div>

    <!-- Sección de despachos -->
    <!-- <div 
      v-if="showDispatchSection"
      class="mt-6 p-6 bg-white rounded-lg shadow"
    >
      <h2 class="text-xl font-bold mb-6">Despachos</h2>

      <table class="w-full">
        <thead>
          <tr>
            <th class="py-4 px-6 text-left">Concepto</th>
            <th class="py-4 px-6 text-left">Productos</th>
            <th class="py-4 px-6 text-left">Fecha</th>
            <th class="py-4 px-6 text-left">Responsable</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="dispatch in dispatches"
            :key="dispatch.id"
            class="border-t hover:bg-gray-50 transition-colors"
          >
            <td class="py-3 px-6">Se realizó el despacho al cliente</td>
            <td class="py-3 px-6">
              {{ dispatch.products.map(p => p.product.name).join(', ') }}
            </td>
            <td class="py-3 px-6">{{ formatDate(dispatch.date) }}</td>
            <td class="py-3 px-6">
              {{ getResponsibleName(dispatch.responsible) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div> -->

    <!-- Botón de anular orden -->
    <div 
      v-if="canCancelOrder"
      class="mt-6 p-6 bg-white rounded-lg shadow text-right"
    >
      <button
        @click="cancelOrder"
        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
      >
        Anular Orden
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput.vue'
import PaymentBox from '../../Shared/PaymentBox.vue'

export default {
  name: 'OrderShow',

  components: {
    Head,
    Link,
    TextInput,
    PaymentBox
  },

  layout: Layout,

  props: {
    order: {
      type: Object,
      required: true
    },
    dispatches: {
      type: Object,
      required: true
    },
    payment_methods: {
      type: Object,
      required: true
    },
    change: {
      type: Number,
      required: true
    },
    cop_change: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      role: this.$parent.auth.user.role,
      selectedCurrency: 'cop',
      numeroWhatsapp: '',
      whatsappCurrency: 'cop',
      form: this.$inertia.form({
        amount: 0,
        method: '',
        order_id: this.order.id,
        reference: '',
      }),
      products: this.$inertia.form({
        products: this.order.products,
        order_id: this.order.id,
      }),
    }
  },

  mounted() {
    const detected = this.detectOrderCurrency(this.order)
    this.selectedCurrency = detected
    this.whatsappCurrency = detected
    if (this.order.client && this.order.client.phone && this.order.client.phone !== '0414409888391') {
      this.numeroWhatsapp = this.order.client.phone
    }
  },

  computed: {
    canManagePayments() {
      return ['financial', 'admin', 'sales'].includes(this.role) && 
             this.order.order_state !== 'cancel' && 
             this.order.order_state !== 'estimate'
    },

    showDispatchSection() {
      return this.order.order_state !== 'cancel' && 
             this.order.order_state !== 'estimate'
    },

    canCancelOrder() {
      return this.order.order_state !== 'cancel' && this.role == 'admin'
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    cancelOrder() {
      if (confirm('¿Está seguro que desea anular esta orden?')) {
        window.location.href = `/orders/${this.order.id}/cancel`
      }
    },

    addDispatch() {
      this.products.post('/orders/dispatch')
    },

    changeOrderState() {
      this.$inertia.get(`/orders/${this.order.id}/changeState`)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getOrderStateLabel(state) {
      const labels = {
        estimate: 'PRESUPUESTO',
        cancel: 'ANULADA',
        request: 'PENDIENTE POR PAGO',
        finish: 'FINALIZADA',
        pay: 'PAGADA',
        delivery: 'EN ESPERA DE DESPACHO'
      }
      return labels[state] || state
    },

    getResponsibleName(responsible) {
      if (!responsible) return ''
      return `${responsible.first_name} ${responsible.last_name}`
    },

    detectOrderCurrency(order) {
      if (order && order.pays && order.pays.length > 0) {
        let hasCop = false
        let hasBs = false
        let hasUsd = false

        order.pays.forEach(p => {
          const m = (p.method || '').toLowerCase()
          const amtCop = parseFloat(p.amount_cop) || 0
          const amtBs = parseFloat(p.amount_bs) || 0
          const amtUsd = parseFloat(p.amount) || 0

          const isUsdMethod = m.includes('dolar') || m.includes('dollar') || m.includes('usd') || m.includes('zelle')
          const isCopMethod = m.includes('peso') || m.includes('cop') || m.includes('bancolombia') || m.includes('nequi') || m.includes('daviplata')
          const isBsMethod = m.includes('bs') || m.includes('bolivar') || m.includes('pago móvil') || m.includes('pago movil') || m.includes('punto')

          if (isUsdMethod) {
            hasUsd = true
          } else if (isCopMethod || (amtCop > 0 && !isBsMethod)) {
            hasCop = true
          } else if (isBsMethod || (amtBs > 0 && !isCopMethod)) {
            hasBs = true
          } else if (amtUsd > 0) {
            hasUsd = true
          }
        })

        const count = (hasCop ? 1 : 0) + (hasBs ? 1 : 0) + (hasUsd ? 1 : 0)
        if (count > 1) return 'mixto'
        if (hasCop) return 'cop'
        if (hasBs) return 'bs'
        if (hasUsd) return 'usd'
      }

      if (order && parseFloat(order.total_cop) > 0) {
        return 'cop'
      }
      return 'usd'
    },

    getPayDetails(pay) {
      const m = (pay.method || '').toLowerCase()
      const amtCop = parseFloat(pay.amount_cop) || 0
      const amtBs = parseFloat(pay.amount_bs) || 0
      const amtUsd = parseFloat(pay.amount) || 0

      const isUsdMethod = m.includes('dolar') || m.includes('dollar') || m.includes('usd') || m.includes('zelle')
      const isCopMethod = m.includes('peso') || m.includes('cop') || m.includes('bancolombia') || m.includes('nequi') || m.includes('daviplata')
      const isBsMethod = m.includes('bs') || m.includes('bolivar') || m.includes('pago móvil') || m.includes('pago movil') || m.includes('punto')

      let type = 'usd'
      if (isUsdMethod) {
        type = 'usd'
      } else if (isCopMethod || (amtCop > 0 && !isBsMethod)) {
        type = 'cop'
      } else if (isBsMethod || (amtBs > 0 && !isCopMethod)) {
        type = 'bs'
      }

      let rateText = null
      if (type === 'cop' && amtUsd > 0 && amtCop > 0) {
        rateText = `${Math.round(amtCop / amtUsd).toLocaleString('es-CO')} COP/$`
      } else if (type === 'bs' && amtUsd > 0 && amtBs > 0) {
        rateText = `${(amtBs / amtUsd).toFixed(2)} Bs/$`
      } else if (type === 'usd' && amtCop > 0 && amtUsd > 0 && amtCop !== amtUsd) {
        rateText = `${Math.round(amtCop / amtUsd).toLocaleString('es-CO')} COP/$`
      }

      return { type, amtCop, amtBs, amtUsd, rateText }
    },

    sendWhatsApp() {
      const curr = this.whatsappCurrency || this.detectOrderCurrency(this.order)
      const numInput = this.numeroWhatsapp || (this.order.client ? this.order.client.phone : '')
      let rawNum = (numInput || '').replace(/[^0-9]/g, '')
      if (!rawNum) {
        return alert('Por favor ingrese un número de teléfono para enviar el mensaje.')
      }

      let numero = rawNum
      if (rawNum.startsWith('04') && rawNum.length === 11) {
        numero = '58' + rawNum.substring(1)
      } else if ((rawNum.startsWith('412') || rawNum.startsWith('414') || rawNum.startsWith('424') || rawNum.startsWith('416') || rawNum.startsWith('426')) && rawNum.length === 10) {
        numero = '58' + rawNum
      } else if (rawNum.startsWith('3') && rawNum.length === 10) {
        numero = '57' + rawNum
      }

      const mensaje = this.buildWhatsAppMessage(curr)
      const url = `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(mensaje)}`
      window.open(url, '_blank')
    },

    buildWhatsAppMessage(currency) {
      let mensaje = `*Nota de Entrega / Venta #${this.order.number_order ?? this.order.id}*\n`
      if (this.order.client && this.order.client.name && this.order.client.name !== 'Cliente por defecto') {
        mensaje += `*Cliente:* ${this.order.client.name}\n`
      }
      mensaje += `\n*Detalle de Productos:*\n`

      const effChange = this.change > 0 ? this.change : 1
      const effCopRate = (this.order.total > 0 && this.order.total_cop > 0)
        ? (this.order.total_cop / this.order.total)
        : (effChange * (this.cop_change || 1))

      this.order.products.forEach((item, idx) => {
        const qty = item.quantity || 1
        const name = item.product?.name ?? 'Producto'

        if (currency === 'cop') {
          let uPrice = parseFloat(item.price_cop) || 0
          if (uPrice <= 0 && item.product?.price_cop) {
            uPrice = parseFloat(item.product.price_cop)
          }
          if (uPrice <= 0) {
            uPrice = Math.round((item.price || 0) * effCopRate)
          }
          const lineTotal = Math.round((uPrice * qty) / 100) * 100
          mensaje += `${idx+1}. ${name} x${qty} - COP ${lineTotal.toLocaleString('es-CO')}\n`
        } else if (currency === 'bs') {
          const uPrice = (item.price || 0) * effChange
          const lineTotal = uPrice * qty
          mensaje += `${idx+1}. ${name} x${qty} - Bs. ${lineTotal.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
        } else { // usd or mixto
          const uPrice = item.price || 0
          const lineTotal = uPrice * qty
          mensaje += `${idx+1}. ${name} x${qty} - $${lineTotal.toFixed(2)}\n`
        }
      })

      if (this.order.discount > 0) {
        if (currency === 'cop') {
          const disc = Math.round(((this.order.discount * effCopRate) / 100)) * 100
          mensaje += `\n*Descuento:* -COP ${disc.toLocaleString('es-CO')}\n`
        } else if (currency === 'bs') {
          const disc = this.order.discount * effChange
          mensaje += `\n*Descuento:* -Bs. ${disc.toFixed(2)}\n`
        } else {
          mensaje += `\n*Descuento:* -$${Number(this.order.discount).toFixed(2)}\n`
        }
      }

      mensaje += `\n*TOTAL:*\n`
      if (currency === 'cop') {
        const tot = this.order.total_cop > 0 ? Number(this.order.total_cop) : Math.round((this.order.total * effCopRate) / 100) * 100
        mensaje += `🪙 COP ${tot.toLocaleString('es-CO')}\n`
        mensaje += `_(Equiv. $${Number(this.order.total).toFixed(2)} USD)_\n`
      } else if (currency === 'bs') {
        const tot = this.order.total_bs > 0 ? Number(this.order.total_bs) : (this.order.total * effChange)
        mensaje += `🏦 Bs. ${tot.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
        mensaje += `_(Equiv. $${Number(this.order.total).toFixed(2)} USD)_\n`
      } else if (currency === 'mixto') {
        const totCop = this.order.total_cop > 0 ? Number(this.order.total_cop) : Math.round((this.order.total * effCopRate) / 100) * 100
        const totBs = this.order.total_bs > 0 ? Number(this.order.total_bs) : (this.order.total * effChange)
        mensaje += `💵 $${Number(this.order.total).toFixed(2)} USD\n`
        mensaje += `🪙 Ref. COP ${totCop.toLocaleString('es-CO')}\n`
        mensaje += `🏦 Ref. Bs. ${totBs.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}\n`
      } else {
        mensaje += `💵 $${Number(this.order.total).toFixed(2)} USD\n`
      }

      if (this.order.pays && this.order.pays.length > 0) {
        mensaje += `\n*Forma(s) de Pago:*\n`
        this.order.pays.forEach(p => {
          let refText = p.reference ? ` (Ref: ${p.reference})` : ''
          const details = this.getPayDetails(p)

          if (details.type === 'cop') {
            const tasaText = details.rateText ? ` (Tasa: ${details.rateText})` : ''
            mensaje += `• ${p.method}: COP ${details.amtCop.toLocaleString('es-CO')}${tasaText} -> Equiv. $${details.amtUsd.toFixed(2)}${refText}\n`
          } else if (details.type === 'bs') {
            const tasaText = details.rateText ? ` (Tasa: ${details.rateText})` : ''
            mensaje += `• ${p.method}: Bs. ${details.amtBs.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}${tasaText} -> Equiv. $${details.amtUsd.toFixed(2)}${refText}\n`
          } else {
            const tasaText = details.rateText ? ` (Tasa: ${details.rateText})` : ''
            mensaje += `• ${p.method}: $${details.amtUsd.toFixed(2)} USD${tasaText}${refText}\n`
          }
        })
      }

      mensaje += `\n¡Gracias por su compra!`
      return mensaje
    }
  }
}
</script>
